import React from 'react';
import makeStyles from '@mui/styles/makeStyles';
import ReadStory from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/@legacy/ReadStory';
import ZoomWrapper from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/components/ZoomWrapper';
import ZoomProvider from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/providers/ZoomProvider';
import ZoomOverlay from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/Zoom/components/ZoomOverlay';
import { ReadAloud } from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/ReadAloud';
import {
  ReadingType,
  BookReadingData,
  ReadingProps,
} from '@monorepo/shared/node_modules/@lwtears/lwt-common-frontend/lib/types';
import useTelemetry from '@monorepo/shared/src/hooks/use-telemetry';
import {
  ApplicationSection,
  PageLayout,
} from '@monorepo/shared/src/graph/schema-generated-types-student';
import { ActivityTypes, BookParams, TelemetryEvents } from '@monorepo/shared/src/types';
import usePage from '@monorepo/shared/src/hooks/use-page';
import useReadStoryStore from '@monorepo/shared/src/graph/state/query-hooks/use-read-story-store';
import BookNavigationControl from '@monorepo/shared/src/components/modules/Books/BookNavigationControl';
import useReadAloudStore from '@monorepo/shared/src/graph/state/query-hooks/use-read-aloud-store';
import usePageViewAutoSwitch from '@monorepo/shared/src/hooks/use-page-view-auto-switch';
import WithQuery from 'components/layout/WithQuery';
import useReadingLog from 'state/hooks/use-reading-log';
import useMinimumPageReadingTime from 'state/hooks/use-minimum-page-reading-time';
import useAudioInstructions from 'state/hooks/use-audio-instructions';
import { formatCharacter, formatPageNumber } from '@monorepo/shared/src/util/telemetry-util';

type Props = {
  bookParams: BookParams;
  readingType: ReadingType;
};

type ReadingConfig = {
  store: (bookParams: BookParams) => [BookReadingData];
  ReadComponent: React.FC<ReadingProps>;
};

const readingConfig: { [key in ReadingType]: ReadingConfig } = {
  [ReadingType.ReadStory]: {
    store: useReadStoryStore,
    ReadComponent: ReadStory,
  },
  [ReadingType.ReadAloud]: {
    store: useReadAloudStore,
    ReadComponent: ReadAloud,
  },
};

const BookContent: React.FC<Props> = ({ bookParams, readingType }) => {
  const classes = useStyles();
  const { store, ReadComponent } = readingConfig[readingType];
  const [{ noOfPages }] = usePage();
  const [{ book, pages, instructions, loading, error }] = store(bookParams);
  const [, { fireEvent: fireAccessActivityPageEvent }] = useTelemetry(
    TelemetryEvents.ACCESS_ACTIVITY_PAGE
  );
  const [, { fireEvent: fireReadBookEvent }] = useTelemetry(TelemetryEvents.READ_BOOK);

  const createTelemetryEventFn = (type: string, eventData: any) => {
    const singlePage = noOfPages === 1;
    const isAccessActivityPage = type === 'accessActivityPage';

    const readBookTelemData = {
      ...eventData,
      activityType:
        eventData?.activityType === 'readStory'
          ? ActivityTypes.READ_STORY
          : ActivityTypes.READ_ALOUD,
      pageLayout: singlePage ? PageLayout.SINGLE_PAGE : PageLayout.DOUBLE_PAGE,
      character: formatCharacter(eventData?.character),
      pageNumber: formatPageNumber(eventData?.pageNumber),
      section: ApplicationSection.LETTER_LEARNING,
    };

    if (isAccessActivityPage) {
      delete readBookTelemData.bookType;
    }

    if (!readBookTelemData?.pageId) return;

    if (isAccessActivityPage) {
      fireAccessActivityPageEvent(readBookTelemData);
      return;
    }

    fireReadBookEvent(readBookTelemData);
  };

  const bookData = {
    book,
    pages,
    loading,
    error,
    createTelemetryEventFn: createTelemetryEventFn,
  };

  useReadingLog();
  useMinimumPageReadingTime();
  usePageViewAutoSwitch();
  useAudioInstructions(instructions, !loading && !error);

  return (
    <WithQuery loading={loading} error={error}>
      <BookNavigationControl navButtonProps={{ className: classes.navButton }} />
      <ZoomProvider>
        <ZoomWrapper>
          <ReadComponent singlePage={noOfPages === 1} bookData={bookData} isOpen={false} />
          <ZoomOverlay />
        </ZoomWrapper>
      </ZoomProvider>
    </WithQuery>
  );
};

const useStyles = makeStyles((theme) => ({
  navButton: {
    color: theme.palette.link.main,
  },
}));

export default BookContent;
